import { useEffect } from 'react';

/**
 * Production-level SEO Head manager component for ShareRecipe.
 * Dynamically updates page titles, meta tags, canonical links, OpenGraph, Twitter Cards,
 * and JSON-LD Schema structured data for search engine indexing.
 */
export const SEOHead = ({
  title = 'ShareRecipe — Discover Healthy Recipes, Join Recipe Clubs & Share Home Cooking',
  description = 'Explore 10,000+ healthy recipes, join interactive Recipe Clubs, get automated grocery checklists, and share delicious home cooking with a vibrant global community. Download the official ShareRecipe app on iOS and Android.',
  keywords = 'healthy recipes, recipe clubs, home cooking, meal planning, vegan recipes, keto meals, sourdough baking, grocery checklists, food community, ShareRecipe app',
  canonicalUrl = 'https://sharecipe.net/',
  ogImage = 'https://sharecipe.net/logo192.png',
  ogType = 'website',
  jsonLd = null,
}) => {
  useEffect(() => {
    // 1. Update Title
    document.title = title;

    // Helper to set or create meta tags
    const setMetaTag = (selector, attribute, value) => {
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        const match = selector.match(/\[(name|property)="([^"]+)"\]/);
        if (match) {
          element.setAttribute(match[1], match[2]);
        }
        document.head.appendChild(element);
      }
      element.setAttribute(attribute, value);
    };

    // Helper to set or create link tags
    const setLinkTag = (rel, href) => {
      let element = document.querySelector(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    // 2. Primary Meta Tags
    setMetaTag('meta[name="description"]', 'content', description);
    setMetaTag('meta[name="keywords"]', 'content', keywords);
    setMetaTag(
      'meta[name="robots"]',
      'content',
      'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
    );
    setMetaTag('meta[name="theme-color"]', 'content', '#402864');
    setLinkTag('canonical', canonicalUrl);

    // 3. OpenGraph Social Meta Tags
    setMetaTag('meta[property="og:title"]', 'content', title);
    setMetaTag('meta[property="og:description"]', 'content', description);
    setMetaTag('meta[property="og:type"]', 'content', ogType);
    setMetaTag('meta[property="og:url"]', 'content', canonicalUrl);
    setMetaTag('meta[property="og:image"]', 'content', ogImage);
    setMetaTag('meta[property="og:site_name"]', 'content', 'ShareRecipe');

    // 4. Twitter Card Meta Tags
    setMetaTag('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMetaTag('meta[name="twitter:title"]', 'content', title);
    setMetaTag('meta[name="twitter:description"]', 'content', description);
    setMetaTag('meta[name="twitter:image"]', 'content', ogImage);
    setMetaTag('meta[name="twitter:site"]', 'content', '@ShareRecipeApp');

    // 5. Inject Dynamic JSON-LD Structured Data Schema
    const schemaId = 'dynamic-jsonld-schema';
    let scriptTag = document.getElementById(schemaId);
    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.id = schemaId;
      scriptTag.type = 'application/ld+json';
      document.head.appendChild(scriptTag);
    }

    const defaultSchemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'ShareRecipe',
        url: 'https://sharecipe.net/',
        description:
          'Discover healthy recipes, join recipe clubs, and share home cooking with a vibrant community.',
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://sharecipe.net/recipes?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'ShareRecipe App',
        operatingSystem: 'iOS, Android',
        applicationCategory: 'FoodAndDrinkApplication',
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          ratingCount: '1240',
        },
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
        },
        downloadUrl: [
          'https://apps.apple.com/pl/app/sharecipe/id6470614602',
          'https://play.google.com/store/apps/details?id=com.sharecipe',
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'ShareRecipe',
        url: 'https://sharecipe.net/',
        logo: 'https://sharecipe.net/logo192.png',
        sameAs: [
          'https://apps.apple.com/pl/app/sharecipe/id6470614602',
          'https://play.google.com/store/apps/details?id=com.sharecipe',
        ],
      },
    ];

    const finalSchemas = jsonLd ? [...defaultSchemas, jsonLd] : defaultSchemas;
    scriptTag.textContent = JSON.stringify(finalSchemas);
  }, [title, description, keywords, canonicalUrl, ogImage, ogType, jsonLd]);

  return null;
};
